import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../services/alert.service';
import { BlackListService } from '../../../services/blacklist.service';
import Input from '../../../components/forms/input.component';
import Switch from '../../../components/forms/switch.component';
import Button from '../../../components/forms/button.component';
import Icon from '../../../components/forms/icon.component';
import useModal from '../../../hooks/use-close-modal.hook';
import Tooltip from '../../../components/forms/tooltip.component';
import LogData from '../../../components/logs/log-data.component';

interface EditAccountProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

function EditAccount({ show, onRequestClose, payload }: EditAccountProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  useModal(modalRef, show);

  const defaultValues = {
    _id: '',
    name: '',
    priority: false
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const submitData = async (data: any, buttonId: string) => {
    const requestBody = {
      name: data.name,
      priority: data.priority
    };

    const confirmed = await AlertService.question(
      t('general.atention'),
      `¿Deseas actualizar la información de la lista negra: ${payload?.name} ?`,
      null as any
    );
    if (!confirmed) {
      return;
    }

    try {
      await BlackListService.updateBlackList(payload?._id, requestBody);
      LogData(requestBody, buttonId, window.location.href);
      AlertService.toastSuccess(
        '¡ Éxito !, los datos de tu lista negra se actualizaron correctamente.'
      );
      onRequestClose(true);
      reset(defaultValues);
    } catch (errorCode: any) {
      console.log('Error: ', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    reset({
      _id: payload?._id ? payload?._id : defaultValues?._id,
      name: payload?.name ? payload?.name : defaultValues?.name,
      priority: payload?.priority ? payload?.priority : defaultValues?.priority
    });
  }, [payload]);

  return (
    <>
      <div aria-hidden="true" className="modal fade" ref={modalRef} data-bs-backdrop="static">
        <div className="modal-dialog modal-mw-900px modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header justify-content-end border-0 pb-0">
              <div
                className="btn btn-sm btn-icon btn-active-light-primary"
                onClick={() => onRequestClose(false)}
              >
                <Icon icon="ki-cross" size="fs-1" />
              </div>
            </div>
            <div className="modal-body pt-0 pb-0 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h2>
                  Editar datos
                  <br />
                  <span className="text-primary">{payload?.name}</span>
                </h2>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>
              <form
                onSubmit={handleSubmit((data) => submitData(data, 'editBlackListData-7273647584'))}
              >
                <div className="fv-row mb-1">
                  <div className="fv-row mb-10">
                    <div className="fw-bolder mb-3 fs-3 required">{t('blacklist-page.name')}</div>
                    <Input
                      requiredIndicator="required"
                      placeholder={t('blacklist-page.name-placeholder')}
                      errors={errors?.name}
                      {...register('name', {
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>
                  <div className="d-flex flex-stack cursor-pointer mb-3">
                    <div className="d-flex align-items-center mb-5">
                      <span className="symbol me-e6 symbol-label"></span>
                      <div className="d-flex flex-column">
                        <div className="fw-bolder fs-3">
                          Lista negra prioritaria{' '}
                          <Tooltip
                            title="Al activar esta opción, esta lista negra se seleccionará
                                       automáticamente al crear tus campañas. Esto garantiza que los números
                                       asignados a esta lista nunca reciban un mensaje al enviar tus
                                       campañas."
                            className="d-inline-block cursor-pointer"
                          >
                            <button
                              className="btn btn-icon btn-transparent pulse pulse-info"
                              type="button"
                            >
                              <Icon
                                icon="ki-information-3"
                                size="fs-1"
                                className="fw-bolder me-2"
                                type="info"
                              />
                              <span className="pulse-ring me-2" />
                            </button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <span className="form-check form-check-custom form-check-solid">
                      <Switch {...register('priority')} />
                    </span>
                  </div>
                  <div className={`d-flex justify-content-end mb-10`}>
                    <Button
                      type="submit"
                      id="sendData"
                      className="btn btn-primary hover-scale mt-10 ms-2 w-20 fw-bolder"
                      disabled={!isValid}
                      isLoading={isSubmitting}
                    >
                      {t('general.save')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditAccount;
